import { useCallback } from 'react'

/**
 * Whether a keyboard event originated inside a text input. Used to avoid
 * stealing Enter / Space (and arrow navigation) when the reply textarea has
 * focus.
 */
const isTextInputElement = (target: HTMLElement): boolean => {
  return (
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'INPUT' ||
    target.isContentEditable
  )
}

/**
 * Whether a text input holds content beyond a bare username mention. A
 * mention-only value (e.g. `@buffer `) counts as empty so arrow keys can
 * still navigate between cards from an untouched reply box. Supports all
 * platform handle formats (Instagram, Facebook, Threads, Bluesky, TikTok).
 */
const hasTextContent = (target: HTMLElement): boolean => {
  let value = ''

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  ) {
    value = target.value
  } else if (target.isContentEditable) {
    value = target.textContent ?? ''
  }

  if (value.length === 0) {
    return false
  }

  // Matches "@username" or "@username " across platform handle formats.
  const mentionOnlyPattern = /^@[\w.-]+\s?$/

  return !mentionOnlyPattern.test(value)
}

type UseEngagementInteractionProps = {
  /** The engagement (comment / mention) id this card represents. */
  id: string
  /**
   * Whether the card is currently expanded. Only consulted to gate the click
   * no-op (see `ignoreClickWhenExpanded`); the keyboard handlers don't read it.
   */
  expanded?: boolean
  onManualExpansion?: (id: string | null) => void
  onExpandNext?: (fromId?: string, onlyRepliable?: boolean) => void
  onExpandPrevious?: () => void
  onHoverReset?: () => void
  /**
   * Clicks originating inside an element matching this selector are ignored, so
   * embedded controls (reactions, inline actions) handle their own interaction
   * instead of toggling the card. Each caller passes the selector(s) for its
   * own controls; omit to disable the exclusion.
   */
  reactionSelector?: string
  /**
   * When true, clicking an already-expanded card is a no-op (mention
   * behaviour). When false (default), every click re-fires `onManualExpansion`
   * and relies on it being idempotent (comment behaviour). This is the sole
   * behavioural divergence between the two cards' click handling, preserved
   * verbatim pending Wave 2 convergence.
   */
  ignoreClickWhenExpanded?: boolean
}

type UseEngagementInteractionReturn = {
  handleClick: (e: Pick<React.MouseEvent, 'target'>) => void
  handleExpandNextFromAction: () => void
  handleCollapse: () => void
  handleKeyDown: (event: React.KeyboardEvent) => void
}

/**
 * Shared click and keyboard handling for an engagement card (comment or
 * mention): expand on click / Enter / Space, collapse on Escape, and
 * ArrowDown / ArrowUp to move between cards.
 *
 * Takes the engagement id and navigation callbacks directly rather than
 * reading a feature-specific context, so both `useCommentInteraction` and
 * `useMentionInteraction` can wrap it. Collapse happens only via Escape, an
 * outside click, or opening a different card — keeping clicks inside an
 * expanded reply form (textarea, footer buttons) from collapsing it.
 */
export const useEngagementInteraction = ({
  id,
  expanded = false,
  onManualExpansion,
  onExpandNext,
  onExpandPrevious,
  onHoverReset,
  reactionSelector,
  ignoreClickWhenExpanded = false,
}: UseEngagementInteractionProps): UseEngagementInteractionReturn => {
  const expand = useCallback(() => {
    onManualExpansion?.(id)
  }, [id, onManualExpansion])

  const handleClick = useCallback(
    (e: Pick<React.MouseEvent, 'target'>) => {
      // Embedded controls (reactions, inline actions) handle their own
      // interaction — skip toggling when the click originated inside one.
      // Use `Element` (not `HTMLElement`) so an SVG icon target inside a
      // control still resolves via `closest` instead of expanding the card.
      const target = e.target
      if (
        reactionSelector &&
        target instanceof Element &&
        target.closest(reactionSelector)
      ) {
        return
      }
      if (ignoreClickWhenExpanded && expanded) return
      expand()
    },
    [expand, reactionSelector, ignoreClickWhenExpanded, expanded],
  )

  // Called after this card is actioned (dismiss / reply): jump to the next
  // repliable card, skipping notify-only / resolved neighbours.
  const handleExpandNextFromAction = useCallback(() => {
    onExpandNext?.(id, true)
  }, [onExpandNext, id])

  const handleCollapse = useCallback(() => {
    onHoverReset?.() // Reset hover state when collapsing
    onManualExpansion?.(null)
  }, [onHoverReset, onManualExpansion])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      const target = event.target instanceof HTMLElement ? event.target : null
      const isInTextInput = target ? isTextInputElement(target) : false
      const hasContent = target ? hasTextContent(target) : false

      // Allow card navigation only when not in a text input, or when the reply
      // box is effectively empty — otherwise arrow keys belong to the
      // textarea's own cursor movement.
      const canNavigate = !isInTextInput || !hasContent

      // For arrow keys with content in a text input: stop here so the wrapper
      // doesn't interfere with the textarea's own navigation.
      if (
        (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
        !canNavigate
      ) {
        return
      }

      if (event.key === 'ArrowDown' && canNavigate) {
        event.preventDefault()
        event.stopPropagation()
        onExpandNext?.()
      } else if (event.key === 'ArrowUp' && canNavigate) {
        event.preventDefault()
        event.stopPropagation()
        onExpandPrevious?.()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (event.currentTarget instanceof HTMLElement) {
          event.currentTarget.focus()
        }
        handleCollapse()
      } else if (
        (event.key === 'Enter' || event.key === ' ') &&
        event.target === event.currentTarget
      ) {
        // Only self-activate when the card wrapper itself holds focus. Gating
        // on the target (rather than "any non-text-input") leaves Enter/Space
        // to nested buttons and links inside the expanded card instead of
        // swallowing their activation.
        event.preventDefault()
        event.stopPropagation()
        expand()
      }
    },
    [onExpandNext, onExpandPrevious, handleCollapse, expand],
  )

  return {
    handleClick,
    handleExpandNextFromAction,
    handleCollapse,
    handleKeyDown,
  }
}
